import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  KAHUN_PLATFORM,
  LOGIN_STAGE,
  LoginWidget,
  LoginWidgetLoginProps,
  LoginWidgetNewPasswordProps,
  LoginWidgetSignUpProps,
  LoginWidgetVerifyEmailProps,
} from "@kahunmedical/user-management";
import { CLINIC_MANAGE_URL } from "../clinicSettings/clinicUrls";
import { getLastSelectedClinicId } from "../clinicManage";
import { ClinicOnboardView } from "./clinicOnboardView";
import Alert from "@mui/lab/Alert";
import { ClinicRoute } from "../../../clinic/ClinicRoutes";

interface ClinicLoginProps {
  path: string;
  onLogin: (clinicId?: string) => void;
  onSignupComplete: () => void;
  onPasswordReset: () => void;
  onVerifyEmail: () => void;
}

const ClinicLogin = ({
  onLogin,
  path,
  onPasswordReset,
  onSignupComplete,
  onVerifyEmail,
}: ClinicLoginProps) => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const stage = useMemo(() => {
    switch (path) {
      case `*/${ClinicRoute.Login}`:
        return LOGIN_STAGE.Login;
      case `*/${ClinicRoute.Signup}`:
        return LOGIN_STAGE.Signup;
      case `*/${ClinicRoute.ForgotPassword}`:
        return LOGIN_STAGE.ForgotPassword;
      case `*/${ClinicRoute.Verify}`:
        return LOGIN_STAGE.VerifyEmail;
      default:
        return LOGIN_STAGE.Login;
    }
  }, [path]);
  const isMounted = useRef<boolean>(true);

  useEffect(() => {
    return () => void (isMounted.current = false);
  }, []);

  const signUpFormProps: LoginWidgetSignUpProps = {
    onSignUpComplete: onSignupComplete,
    requireFullName: false,
    requireSpecialty: false,
    requireTermsConfirm: true,
    customClientData: {
      verifyUrl: `https://${window.location.hostname}${CLINIC_MANAGE_URL}/${ClinicRoute.Verify}`,
    },
  };

  const loginFormProps: LoginWidgetLoginProps = {
    onSignIn: async (username) => {
      setIsRedirecting(true);
      try {
        // Avoid warnings exit the function if component got unmounted
        if (!isMounted.current) return;

        let requestedClinicId = sessionStorage.getItem("requestedClinicId"); //get the saved clinic id from before login
        if (requestedClinicId) sessionStorage.removeItem("requestedClinicId");
        else requestedClinicId = getLastSelectedClinicId(username); //otherwise get the last viewed clinic

        onLogin(requestedClinicId || "");
      } finally {
        if (isMounted.current) setIsRedirecting(false);
      }
    },
    keepLoading: isRedirecting,
    enableGoogleLogin: true,
    socialLoginRedirect: `${window.location.origin}${CLINIC_MANAGE_URL}`,
  };

  const newPasswordFormProps: LoginWidgetNewPasswordProps = {
    onNewPasswordComplete: onPasswordReset,
  };

  const verifyEmailProps: LoginWidgetVerifyEmailProps = {
    onComplete: onVerifyEmail,
  };

  return (
    <LoginWidget
      stage={stage}
      customContainer={ClinicOnboardView}
      signUpFormProps={signUpFormProps}
      loginFormProps={loginFormProps}
      newPasswordFormProps={newPasswordFormProps}
      verifyEmailProps={verifyEmailProps}
      platform={KAHUN_PLATFORM.ClinicManage}
      trackEvents={true}
      message={
        isRedirecting && <Alert severity="success">Redirecting...</Alert>
      }
    />
  );
};

export default ClinicLogin;
